import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Divider,
  Collapse,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { DSLConfig, ValidationResult } from '../../types/dynamicRouting';
import RulesEditor from './RulesEditor';
import GatewaysEditor from './GatewaysEditor';

import {closestCenter, DndContext} from '@dnd-kit/core'
import { arrayMove } from '@dnd-kit/sortable';

interface ConfigEditorProps {
  config: DSLConfig;
  onChange: (config: DSLConfig) => void;
  validationResult?: ValidationResult;
  collapseSignal?: number;
}

const ConfigEditor: React.FC<ConfigEditorProps> = ({
  config,
  onChange,
  validationResult,
  collapseSignal,
}) => {
  const [basicConfigExpanded, setBasicConfigExpanded] = useState(true);

  React.useEffect(() => {
    if (collapseSignal !== undefined) {
      setBasicConfigExpanded(false);
    }
  }, [collapseSignal]);

  const handleBasicInfoChange = (field: keyof DSLConfig, value: string) => {
    onChange({
      ...config,
      [field]: value,
    });
  };

  const handleRulesChange = (rules: DSLConfig['rules']) => {
    onChange({
      ...config,
      rules,
    });
  };

  const handleGatewaysChange = (gateways: DSLConfig['gateways']) => {
    onChange({
      ...config,
      gateways,
    });
  };

  // Helper function to get validation messages for a specific path
  const getValidationMessages = (path: string) => {
    if (!validationResult) return [];
    return validationResult.messages.filter(msg => 
      msg.path.includes(path) || msg.path === path
    );
  };

  // helper function for drage and drop 
  const handleDragEnd = (event:any) =>{
    const {active, over} = event;

  // no drop target → bail out
  if (!over) return;

  // bail out if drag/drop positions are same
  if (active.id === over.id) return;

  const oldIndex = config.rules.findIndex((rule) => rule.id === active.id);
  const newIndex = config.rules.findIndex((rule) => rule.id === over.id);

  if (oldIndex === -1 || newIndex === -1) return;

  const newRules = arrayMove(config.rules, oldIndex, newIndex);

  onChange({
    ...config,
    rules: newRules,
  });

  }

  return (
    <Box>
      {/* Basic Configuration - Collapsible */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 2,
          bgcolor: 'grey.50',
          borderRadius: 1,
          cursor: 'pointer'
        }}
        onClick={() => setBasicConfigExpanded(!basicConfigExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Basic Configuration
          </Typography>
          {!basicConfigExpanded && (
            <Typography variant="body2" color="text.secondary">
              {config.name || 'Unnamed Configuration'}
            </Typography>
          )}
        </Box>
        {basicConfigExpanded ? <ExpandLess /> : <ExpandMore />}
      </Box>

      <Collapse in={basicConfigExpanded}>
        <Box sx={{ p: 3, pt: 2 }}>
          <Grid container spacing={4}>
            <Grid  size={{xs:12, sm:6, md:4}}>
              <TextField
                fullWidth
                label="Version"
                value={config.version}
                onChange={(e) => handleBasicInfoChange('version', e.target.value)}
                placeholder="1.0.0"
                helperText="Semantic version (e.g., 1.0.0)"
                error={getValidationMessages('version').some(msg => msg.level === 'error')}
              />
            </Grid>
            
            <Grid size={{xs:12, sm:6, md:8}}>
              <TextField
                fullWidth
                label="Configuration Name"
                value={config.name}
                onChange={(e) => handleBasicInfoChange('name', e.target.value)}
                placeholder="My Routing Configuration"
                helperText="Human-readable name for this configuration"
                error={getValidationMessages('name').some(msg => msg.level === 'error')}
              />
            </Grid>
            
            <Grid size={{xs:12}}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description (Optional)"
                value={config.description || ''}
                onChange={(e) => handleBasicInfoChange('description', e.target.value)}
                placeholder="Describe the purpose of this routing configuration..."
                helperText="Optional description of the routing configuration"
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>

      <Divider sx={{ my: 4 }} />

      {/* Gateways Section */}
      <GatewaysEditor
        gateways={config.gateways}
        onChange={handleGatewaysChange}
        validationResult={validationResult}
        collapseSignal={collapseSignal}
      />

      {/* <Divider sx={{ my: 2 }} /> */}

      {/* Rules Section */}
      <Typography variant="h6" gutterBottom>
        Routing Rules
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define the routing rules that determine which gateways to use based on payment conditions.
        Rules can be nested hierarchically for complex routing logic.
      </Typography>
      
      <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCenter}>
      <RulesEditor
        rules={config.rules}
        gateways={config.gateways}
        onChange={handleRulesChange}
        validationResult={validationResult}
        collapseSignal={collapseSignal}
      />    
      </DndContext>
    </Box>
  );
};

export default ConfigEditor;